import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ExplainDeclineReasonModal from './ExplainDeclineReasonModal';
import CounterOrConfirmModal from './CounterOrConfirmModal';
import ConfirmRejectModal from './ConfirmRejectModal';
import { Campaign, Collab } from '@foodfluence/shared';
import { canCounter } from '../../utils/counterUtils';
import { isCreditValidCampaignType } from 'utils/collabUtils';
import ModalShell from '../../../../components/ui/ModalShell';
import { ReasonRadios, SheetButton, SheetFooter, SheetHeader, useBrand } from './sheetParts';

interface DeclineReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onSubmit: (reason: string) => void;
  collab: Collab;
  campaign: Campaign;
  /** When set, "Counter instead" hands the reason here instead of opening the Counter Offer page. */
  onCounter?: (reason: string) => void;
}

const declineOptions = {
  A: 'Not enough compensation',
  B: 'Too many deliverables',
  C: "Doesn't fit my usual brand content",
  D: 'Location is inconvenient',
  E: 'Other',
};
type DeclineKey = keyof typeof declineOptions;
const KEYS = Object.keys(declineOptions) as DeclineKey[];

const DeclineReasonModal: React.FC<DeclineReasonModalProps> = ({ isOpen, onClose, onSubmit, onBack, collab, campaign, onCounter }) => {
  const [selectedReason, setSelectedReason] = useState<DeclineKey | null>(null);
  const [isExplainDeclineModalOpen, setIsExplainDeclineModalOpen] = useState(false);
  const [isCounterOrConfirmModalOpen, setIsCounterOrConfirmModalOpen] = useState(false);
  const [isConfirmRejectModalOpen, setIsConfirmRejectModalOpen] = useState(false);
  const navigate = useNavigate();
  const brand = useBrand(collab, campaign);

  // C and D decline straight away; E asks for text; A and B offer a counter while one is left.
  const declinesNow = selectedReason === 'C' || selectedReason === 'D';

  const handleSubmit = () => {
    if (declinesNow) {
      handleConfirmReject();
    } else if (selectedReason === 'E') {
      setIsExplainDeclineModalOpen(true);
      onClose();
    } else if (canCounter(campaign, collab)) {
      setIsCounterOrConfirmModalOpen(true);
      onClose();
    } else {
      setIsConfirmRejectModalOpen(true);
      onClose();
    }
  };

  const handleConfirmReject = () => {
    onSubmit(declineOptions[selectedReason as DeclineKey]);
    setIsConfirmRejectModalOpen(false);
    onClose();
  };

  const handleExplainSubmit = (explanation: string) => {
    onSubmit(explanation);
    setIsExplainDeclineModalOpen(false);
    onClose();
  };

  const cash = collab?.cash ?? 0;
  const credit = collab?.credit ?? 0;
  const productValue = Math.round(campaign?.productValue ?? 0);
  const offerLine = [
    `$${cash} cash`,
    isCreditValidCampaignType(campaign?.campaignType) || credit > 0 ? `$${credit} credit` : null,
    productValue > 0 ? `$${productValue} product` : null,
  ].filter(Boolean).join(' + ');

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onClose={onClose}
        hideClose
        footer={
          <SheetFooter>
            <SheetButton tone="outline" onClick={onClose}>Cancel</SheetButton>
            <SheetButton tone={declinesNow ? 'red' : 'lime'} onClick={handleSubmit} isDisabled={!selectedReason}>
              {declinesNow ? 'Decline invite' : 'Continue'}
            </SheetButton>
          </SheetFooter>
        }
      >
        <SheetHeader
          title="Decline invite"
          onClose={onClose}
          step={{ index: 1, total: declinesNow ? 1 : 2, name: 'Reason' }}
          sub="Why are you passing?"
        />
        <ReasonRadios
          label="Decline reason"
          options={KEYS.map(k => declineOptions[k])}
          value={selectedReason ? declineOptions[selectedReason] : null}
          onChange={label => setSelectedReason(KEYS.find(k => declineOptions[k] === label) ?? null)}
        />
      </ModalShell>

      <ExplainDeclineReasonModal
        isOpen={isExplainDeclineModalOpen}
        onClose={() => setIsExplainDeclineModalOpen(false)}
        onBack={onBack}
        onSubmit={handleExplainSubmit}
        brandName={brand.name}
      />

      <CounterOrConfirmModal
        isOpen={isCounterOrConfirmModalOpen}
        onClose={() => setIsCounterOrConfirmModalOpen(false)}
        onBack={onBack}
        onCounter={() => {
          setIsCounterOrConfirmModalOpen(false);
          const reason = declineOptions[selectedReason as DeclineKey];
          if (onCounter) onCounter(reason);
          else navigate(`/collabs/counter-collab`, { state: { collab, campaign, reason } });
        }}
        onConfirmReject={handleConfirmReject}
        numTimesCountered={collab?.counters?.length || 0}
        brandName={brand.name}
        offerLine={offerLine}
      />
      <ConfirmRejectModal
        isOpen={isConfirmRejectModalOpen}
        onClose={() => setIsConfirmRejectModalOpen(false)}
        onConfirmReject={handleConfirmReject}
        title={campaign?.name}
        reason={selectedReason ? declineOptions[selectedReason] : undefined}
        brandName={brand.name}
        imageSrc={brand.client?.instagramProfilePicture}
      />
    </>
  );
};

export default DeclineReasonModal;
